import React, { FC, useEffect, useState } from "react";
import CheckboxV2 from "shared/Checkbox/CheckboxV2";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { Formik, Form } from "formik";
import {
  getallupdateProperties,
  getAmenitiesProperty,
  getFeaturesProperty,
  updateProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";
import successHandler from "utils/helpers/SuccessHandler";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { amenitiesvalidationSchema } from "utils/formSchema";
import CommonLayoutV2 from "./CommonLayoutV2";
import { getIconComponent } from "shared/Icon/IconDymamiclisting";
import { AmenitiesCategories } from "redux/reducers/Properties/PropertiesSliceTypes";
import { calculateStepCompletion, checkExistingCompletion } from "utils/helpers/listingCompletionHelper";
import "react-loading-skeleton/dist/skeleton.css";

export interface AmentitesProps {}

interface InitialValues {
  amenities: string[];
  features: string[];
}

const Amentites: FC<AmentitesProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedAmenities, setSelectedAmenities] = useState<any[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<any[]>([]);
  const navigate = useNavigate();
  const { globalId, AmenitiesCategories, featuresCategories, PropertyToEdit } = useSelector(
    (state: RootState) => state.property
  );

  const { slug } = useParams();
  const id = slug;
  const RouteID = id;
  const amenitiesIds = selectedAmenities?.map((item: any) => item.amenity_id);
  const featuresIds = selectedFeatures?.map((item: any) => item.feature_id);
  const [isLoading, setIsLoading] = useState(true);
  const [initialAmenities, setInitialAmenities] = useState<any[]>([]);
  const [initialFeatures, setInitialFeatures] = useState<any[]>([]);
  // ADD THIS: Cache property type in local state
  const [cachedPropertyType, setCachedPropertyType] = useState<string | null>(null);

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false, fieldsCompleted: 0, totalFields: 1 },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  });

  const initialValues = {
    amenities: [],
    features: [],
  };

  const handleAmenitiesChange = (id: number) => {
    if (amenitiesIds?.includes(id)) {
      const remainingAmenities = selectedAmenities?.filter(
        (amenity: any) => amenity.amenity_id !== id
      );
      setSelectedAmenities(remainingAmenities);
    } else {
      const data = {
        amenity_id: id,
      };
      setSelectedAmenities([...selectedAmenities, data]);
    }
  };

  const handleFeaturesChange = (id: number) => {
    if (featuresIds?.includes(id)) {
      const remainingFeatures = selectedFeatures?.filter(
        (feature: any) => feature.feature_id !== id
      );
      setSelectedFeatures(remainingFeatures);
    } else {
      const data = {
        feature_id: id,
      };
      setSelectedFeatures([...selectedFeatures, data]);
    }
  };

  const handleSubmit = (values: any) => {
    const isAmenitiesChanged =
      JSON.stringify(selectedAmenities) !== JSON.stringify(initialAmenities);
    const isFeaturesChanged =
      JSON.stringify(selectedFeatures) !== JSON.stringify(initialFeatures);

    if (isAmenitiesChanged || isFeaturesChanged) {
      const payload = {
        id: id,
        property_amenities_attributes: selectedAmenities,
        property_features_attributes: selectedFeatures,
      };

      dispatch(updateProperties({ propertyId: id, property: payload }))
        .unwrap()
        .then((response) => {
          successHandler(response?.message || "Updated successfully");
          navigate(`/add-listing-7/${slug}`);
        })
        .catch((error) => {
          console.error("Error updating property: ", error);
        });
    } else {
      navigate(`/add-listing-7/${slug}`);
    }
  };

  useEffect(() => {
    dispatch(getAmenitiesProperty());
    dispatch(getFeaturesProperty());
  }, [dispatch]);

  useEffect(() => {
    const propertyId = id;
    if (propertyId) {
      dispatch(getallupdateProperties({ propertyId }))
        .unwrap()
        .then((response) => {
          const prefilledAmenities = (response?.property?.amenities || []).map(
            (amenity: any) => ({ amenity_id: amenity.id })
          );

          const prefilledFeatures = (response?.property?.features || []).map(
            (feature: any) => ({ feature_id: feature.id })
          );

          setSelectedAmenities(prefilledAmenities);
          setSelectedFeatures(prefilledFeatures);

          setInitialAmenities(prefilledAmenities);
          setInitialFeatures(prefilledFeatures);
        });
    }
  }, [dispatch, globalId, id]);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Calculate completion for Step 6 (Amenities)
  useEffect(() => {
    // At least one amenity should be selected
    const amenitiesValue = selectedAmenities.length > 0 ? "filled" : "";
    const mockValues = { amenities: amenitiesValue };
    const requiredFields = ["amenities"];
    const hasErrors = selectedAmenities.length === 0;
    
    const completion = calculateStepCompletion(mockValues, requiredFields, hasErrors);

    setStepCompletionStatus((prev) => ({
      ...prev,
      6: completion,
    }));
  }, [selectedAmenities]);

  // Load completion status from PropertyToEdit
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);
      
      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

   useEffect(() => {
    if (PropertyToEdit?.property_type?.name) {
      setCachedPropertyType(PropertyToEdit.property_type.name);
    }
  }, [PropertyToEdit?.property_type?.name]);

  return (
    <CommonLayoutV2 
      currentHref="/add-listing-6" 
      PropertyID={RouteID}
      propertyType={cachedPropertyType}  
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Amenities & Features
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Select amenities and features that make your property stand out. These help guests find exactly what they need.
          </p>
        </div>

        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ touched, errors, isSubmitting }) => (
            <Form className="space-y-8">
              {/* Amenities Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      1
                    </span>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Property Amenities *
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Select at least one amenity that your property offers
                      </p>
                    </div>
                  </div>
                  {selectedAmenities.length > 0 && (
                    <div className="bg-[#F56948] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {selectedAmenities.length} selected
                    </div>
                  )}
                </div>

                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array(9)
                      .fill(0)
                      .map((_, index) => (
                        <div
                          key={index}
                          className="h-16 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse"
                        />
                      ))}
                  </div>
                ) : AmenitiesCategories.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {AmenitiesCategories?.map((amenity: AmenitiesCategories) => {
                      const isChecked = amenitiesIds.includes(amenity?.id);
                      return (
                        <div
                          key={amenity?.id}
                          className={`relative rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                            isChecked
                              ? "border-[#F56948] bg-orange-50 dark:bg-orange-900/10"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        >
                          <label
                            htmlFor={`amenity-${amenity?.id}`}
                            className="flex items-center gap-3 p-4 cursor-pointer"
                          >
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                              isChecked
                                ? "bg-[#F56948] text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                            }`}>
                              {amenity?.icon_name?.includes("http") ? (
                                <img
                                  src={amenity?.icon_name}
                                  alt={amenity?.name}
                                  className="w-6 h-6"
                                />
                              ) : (
                                getIconComponent(amenity?.icon_name ?? "") &&
                                React.createElement(
                                  getIconComponent(amenity?.icon_name ?? ""),
                                  { className: "text-2xl" }
                                )
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">
                              {amenity.name}
                            </span>
                            <input
                              id={`amenity-${amenity?.id}`}
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={() => handleAmenitiesChange(amenity?.id)}
                            />
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isChecked
                                ? "bg-[#F56948] border-[#F56948]"
                                : "border-gray-300 dark:border-gray-600"
                            }`}>
                              {isChecked && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <p>No amenities available at the moment.</p>
                  </div>
                )}
              </div>

              {/* Features Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      2
                    </span>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Property Features
                        <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">(Optional)</span>
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Highlight special features that set your property apart
                      </p>
                    </div>
                  </div>
                  {selectedFeatures.length > 0 && (
                    <div className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {selectedFeatures.length} selected
                    </div>
                  )}
                </div>

                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array(6)
                      .fill(0)
                      .map((_, index) => (
                        <div
                          key={index}
                          className="h-16 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse"
                        />
                      ))}
                  </div>
                ) : featuresCategories && featuresCategories.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {featuresCategories?.map((feature: any) => {
                      const isChecked = featuresIds.includes(feature?.id);
                      return (
                        <div
                          key={feature?.id}
                          className={`relative rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                            isChecked
                              ? "border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-700/50"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        >
                          <label
                            htmlFor={`feature-${feature?.id}`}
                            className="flex items-center gap-3 p-4 cursor-pointer"
                          >
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                              isChecked
                                ? "bg-gray-600 dark:bg-gray-500 text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                            }`}>
                              {feature?.icon_name?.includes("http") ? (
                                <img
                                  src={feature?.icon_name}
                                  alt={feature?.name}
                                  className="w-6 h-6"
                                />
                              ) : (
                                getIconComponent(feature?.icon_name) &&
                                React.createElement(
                                  getIconComponent(feature?.icon_name),
                                  { className: "text-2xl" }
                                )
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">
                              {feature?.name}
                            </span>
                            <input
                              id={`feature-${feature?.id}`}
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={() => handleFeaturesChange(feature?.id)}
                            />
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isChecked
                                ? "bg-gray-600 dark:bg-gray-500 border-gray-600 dark:border-gray-500"
                                : "border-gray-300 dark:border-gray-600"
                            }`}>
                              {isChecked && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <p>No features available at the moment.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Step 6 of 9 • Select amenities and features
                </div>
                <div className="flex gap-3">
                  <ButtonSecondary
                    href={`/add-listing-5/${slug}`}
                    disabled={isSubmitting}
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Go Back
                    </span>
                  </ButtonSecondary>
                  <ButtonPrimary 
                    type="submit" 
                    disabled={isSubmitting}
                    className="min-w-[140px]"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Continue
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    )}
                  </ButtonPrimary>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </CommonLayoutV2>
  );
};

export default Amentites;